import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem('user');
    let token = '';
    if (user) {
      try {
        token = JSON.parse(user).token;
      } catch (e) {
        // Fallback to direct token if user is not JSON
        token = localStorage.getItem('token');
      }
    } else {
      token = localStorage.getItem('token');
    }
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const affiliateService = {
  // ============ Affiliate Management ============

  // Get all affiliates
  getAllAffiliates: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/affiliates', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate by ID
  getAffiliateById: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/affiliate/admin/affiliates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Approve affiliate
  approveAffiliate: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/affiliates/${id}/approve`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Reject affiliate
  rejectAffiliate: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/affiliates/${id}/reject`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update affiliate
  updateAffiliate: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/api/affiliate/admin/affiliates/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Suspend affiliate
  suspendAffiliate: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/affiliates/${id}/suspend`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate statistics
  getAffiliateStatistics: async () => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/affiliates/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ Offer Management ============

  // Create offer
  createOffer: async (data) => {
    try {
      const response = await axiosInstance.post('/api/affiliate/admin/offers', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get all offers
  getAllOffers: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/offers', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get offer by ID
  getOfferById: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/affiliate/admin/offers/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update offer
  updateOffer: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/api/affiliate/admin/offers/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete offer
  deleteOffer: async (id) => {
    try {
      const response = await axiosInstance.delete(`/api/affiliate/admin/offers/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Toggle offer status
  toggleOfferStatus: async (id) => {
    try {
      const response = await axiosInstance.patch(`/api/affiliate/admin/offers/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ Earnings Management ============

  // Get all earnings
  getAllEarnings: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/earnings', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Approve earning
  approveEarning: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/earnings/${id}/approve`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Pay earning
  payEarning: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/earnings/${id}/pay`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk approve earnings
  bulkApproveEarnings: async (data) => {
    try {
      const response = await axiosInstance.post('/api/affiliate/admin/earnings/bulk-approve', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk pay earnings
  bulkPayEarnings: async (data) => {
    try {
      const response = await axiosInstance.post('/api/affiliate/admin/earnings/bulk-pay', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Cancel earning
  cancelEarning: async (id, data = {}) => {
    try {
      const response = await axiosInstance.post(`/api/affiliate/admin/earnings/${id}/cancel`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get earnings statistics
  getEarningsStatistics: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/earnings/stats', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // ============ Settings ============

  // Get affiliate settings
  getSettings: async () => {
    try {
      const response = await axiosInstance.get('/api/affiliate/admin/settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update affiliate settings
  updateSettings: async (data) => {
    try {
      const response = await axiosInstance.put('/api/affiliate/admin/settings', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default affiliateService;

